import { Box, Typography, useTheme, Link } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import React, { useMemo } from "react";
import { GET_AVAILABLE_INTEGRATIONS } from "../../../apis/organization-apis/people-apis/peopleApis";
import { GET_INFRA_INTEGRATION_LEVEL_SUMMARY } from "../../../apis/organization-apis/Infra-apis/InfraApis";
import { dispatchSetInfraType } from "./store/dispatcher";
import { useDispatch } from "react-redux";
import useAppStore from "../../../stores";
import InfrastructureCard from "../../../components/InfrastructureCard/InfrastructureCard";
import InfrastructureCardSkeleton from "../../../components/InfrastructureCard/InfrastructureCardSkeleton";
import InformationStepsCard from "../../../components/InformationStepsCard/InformationStepsCard";
import DeactivatedIntegrationsAlert, {
  ALL_INFRA_CLOUD_INTEGRATION_KEYS,
} from "../../../components/DeactivatedIntegrationsAlert/DeactivatedIntegrationsAlert";

const InfraMain = () => {
  const theme = useTheme();
  let navigate = useNavigate();
  let dispatcher = useDispatch();

  const selectedFrameworkId = useAppStore((state) => state.selectedFrameworkId);

  // Get available integrations (AWS, Azure, GCP)
  const { data: availableIntegrationsData, loading: loadingAvailable } =
    useQuery(GET_AVAILABLE_INTEGRATIONS, {
      variables: {
        category: "infra",
      },
    });

  // Get system level summary data, scoped to the currently selected framework
  // so counts match the per-framework totals shown on the checks list page.
  const { data: infraLevelSummaryData, loading: loadingSystemSummary } =
    useQuery(GET_INFRA_INTEGRATION_LEVEL_SUMMARY, {
      variables: {
        framework_id: selectedFrameworkId,
      },
      skip: !selectedFrameworkId,
    });

  // Combine system level summary with integration details
  const infraIntegrations = useMemo(() => {
    const available =
      availableIntegrationsData?.getAvailableIntegrations?.list || [];
    const systemSummaryList =
      infraLevelSummaryData?.getInfraIntegrationLevelSummary?.data || [];

    // Filter to only show AWS, Azure, GCP
    const infraKeys = ["aws", "azure", "gcp"];
    const filtered = available.filter((int) =>
      infraKeys.includes(int.key?.toLowerCase())
    );

    // Map integrations with system level summary data
    return filtered
      .map((integration) => {
        const systemData = systemSummaryList.find(
          (sys) =>
            sys.integration?.toLowerCase() === integration.key?.toLowerCase()
        );

        // Only include if totalChecksApplicable > 0 && totalAccounts > 0
        if (
          systemData &&
          systemData.totalChecksApplicable > 0 &&
          systemData.totalAccounts > 0
        ) {
          return {
            ...integration,
            systemData,
          };
        }
        return null;
      })
      .filter((integration) => integration !== null);
  }, [availableIntegrationsData, infraLevelSummaryData]);

  const handleRedirectOverview = (infra) => {
    navigate("/infrastructure/overview");
    dispatcher(dispatchSetInfraType(infra));
    localStorage.setItem("infraType", infra);
  };

  // Header section component
  const HeaderSection = () => (
    <>
      <Typography variant="h5" noWrap sx={{ fontWeight: "bold" }}>
        Overview
      </Typography>
      <Box display="flex" justifyContent="space-between">
        <Typography sx={{ width: "60%" }} color="text.secondary">
          Streamline infrastructure management with secure cloud integration,
          ensuring seamless access and robust protection for your systems and
          data.
        </Typography>
        <Box></Box>
      </Box>
    </>
  );

  return (
    <div>
      <Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <DeactivatedIntegrationsAlert
            integrationKeys={ALL_INFRA_CLOUD_INTEGRATION_KEYS}
            onManageIntegrations={() =>
              navigate("/integrations?domain=cloud_provider")
            }
          />
          <HeaderSection />
        </Box>

        {/* Information Steps Card */}
        <Box sx={{ mb: 3, mt: 3 }}>
          <InformationStepsCard
            heading="**What should I do on this page?**"
            subtitle="This page provides an overview of your connected cloud infrastructure integrations (AWS, Azure, GCP) and their compliance status."
            steps={[
              "View **infrastructure cards** for each connected cloud provider (AWS, Azure, GCP) showing summary statistics including total accounts, checks applicable, and compliance status.",

              "Each card displays key metrics such as **total accounts**, **checks applicable**, **passing checks**, **failing checks**, and **pending checks** for that cloud provider.",

              "Click on any **infrastructure card** to navigate to the detailed overview page where you can see all resources, accounts, and their individual compliance statuses.",

              "If no infrastructure integrations are shown, connect your cloud providers (AWS, Azure, or GCP) from the **Integrations page** to start monitoring your infrastructure compliance.",

              "Regularly review the compliance status of your infrastructure to ensure all resources meet your security and compliance requirements.",
            ]}
            footerText="💡 Connect all your cloud infrastructure providers to get a comprehensive view of your infrastructure compliance posture."
            collapsible={true}
            defaultExpanded={false}
          />
        </Box>

        <Box
          sx={{
            my: "30px",
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
          }}
        >
          {loadingAvailable || loadingSystemSummary ? (
            // Loading skeleton
            [1, 2, 3].map((index) => <InfrastructureCardSkeleton key={index} />)
          ) : infraIntegrations.length === 0 ? (
            // Empty state
            <Box
              sx={{
                marginTop: "20vh",
                justifyContent: "center",
                alignItems: "center",
                alignSelf: "center",
                textAlign: "center",
                width: "50%",
                py: 8,
                px: 4,
                backgroundColor: "#FFFFFF",
                borderRadius: "12px",
                border: `1px solid ${theme.customColors.border.main}`,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <Typography
                variant="body1"
                color={theme.customColors.text.secondary}
              >
                No connected infrastructure integrations found. Connect AWS,
                Azure, or GCP from the{" "}
                <Link
                  href="/integrations?domain=cloud_provider"
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: "none",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  Integrations page
                </Link>
                .
              </Typography>
            </Box>
          ) : (
            // Infrastructure cards
            infraIntegrations.map((integration) => (
              <InfrastructureCard
                key={integration.key}
                integration={integration}
                systemData={integration.systemData}
                logoPath={integration.logo_path}
                onClick={() => handleRedirectOverview(integration.key)}
              />
            ))
          )}
        </Box>
      </Box>
    </div>
  );
};

export default InfraMain;
